// Aantal contourlijnen bij stickers en folies met contoursnijden.
//
// Print.com kent per product een eigen "geen"-waarde voor deze eigenschap
// (00, no_contour_lines of none). Overal standaard "no_contour_lines" sturen
// gaf bij o.a. Biond en textielstickers een ongeldige waarde; Print.com koos
// dan zelf kisscut en de prijs hoorde niet meer bij wat de klant koos.
// Bij kisscut moet de klant zelf het aantal kunnen kiezen (tot 25 bij
// sommige folies), in plaats van het vaste "1" van vroeger.
//
// Welke afwerking een aantal heeft, verschilt per product en staat in de
// Print.com-excludes: thrucut sluit bij vinylsticker, Biond en de andere
// folies de aantallen 1-25 uit en gaat dus met "geen". Een aantal meesturen
// bij thrucut liet Print.com stil omschakelen naar kisscut (OPTION_DRIFT),
// waardoor thrucut op de productpagina niet te kiezen was. Gewone kisscut
// verschilt wel per product: bij Biond met aantal, bij raamstickers zonder.

export const CONTOUR_LINES_OPTION_ID = 'number_of_contour_lines';

const NONE_VALUES = new Set(['none', 'no_contour_lines', '00', '0', '']);
const STRAIGHT_CUT_SHAPES = new Set(['clean_cut_stickers', 'clean_cut', 'none', '']);
// Zonder excludes: kisscut-varianten en applicatiefolie hebben een aantal,
// thrucut niet. Andere vormsneden (pet-vilt, freesletters) blijven ongemoeid.
const COUNTED_SHAPE_PATTERN = /kiss_cut|application_foil/;
const UNCOUNTED_SHAPE_PATTERN = /die_cut/;

type ExcludeRule = Array<{ property?: unknown; options?: unknown }>;

export function isContourNoneValue(value: unknown): boolean {
  return NONE_VALUES.has(String(value ?? '').trim().toLowerCase());
}

/** De eigen "geen"-waarde van dit product, of null als het product er geen kent. */
export function contourNoneValue(values: Array<string | number>): string | null {
  const found = values.map(String).find((value) => isContourNoneValue(value));
  return found ?? null;
}

function baseShape(shape: unknown): string {
  return String(shape ?? '').split('|shape:')[0].trim().toLowerCase();
}

/**
 * Contourwaarden die Print.com zonder verdere voorwaarde uitsluit voor deze
 * afwerking. Regels met nog een derde eigenschap (materiaal, maat) gelden maar
 * in een deel van de gevallen en tellen hier niet mee.
 */
function contourValuesExcludedForShape(shape: string, excludes: unknown): Set<string> | null {
  if (!Array.isArray(excludes)) return null;
  let found = false;
  const excluded = new Set<string>();
  for (const rule of excludes as ExcludeRule[]) {
    if (!Array.isArray(rule) || rule.length !== 2) continue;
    const shapePart = rule.find((part) => part?.property === 'custom_shape');
    const contourPart = rule.find((part) => part?.property === CONTOUR_LINES_OPTION_ID);
    if (!shapePart || !contourPart || !Array.isArray(shapePart.options) || !Array.isArray(contourPart.options)) continue;
    found = true;
    if (!shapePart.options.map((option) => String(option).toLowerCase()).includes(shape)) continue;
    for (const option of contourPart.options) excluded.add(String(option));
  }
  return found ? excluded : null;
}

/**
 * Contourwaarden die bij deze afwerking mogen. Bij recht afsnijden en bij
 * thrucut is dat alleen de "geen"-waarde, bij kisscut de aantallen.
 */
export function allowedContourValues(shape: unknown, values: Array<string | number>, excludes?: unknown): string[] {
  const all = values.map(String);
  const shapeValue = baseShape(shape);
  if (STRAIGHT_CUT_SHAPES.has(shapeValue)) return all.filter((value) => isContourNoneValue(value));
  // Draagtrays en freesletters kennen geen "geen": daar is het aantal altijd een keuze.
  if (!all.some((value) => isContourNoneValue(value))) return all;
  const excluded = contourValuesExcludedForShape(shapeValue, excludes);
  if (excluded) return all.filter((value) => !excluded.has(value));
  if (COUNTED_SHAPE_PATTERN.test(shapeValue)) return all.filter((value) => !isContourNoneValue(value));
  if (UNCOUNTED_SHAPE_PATTERN.test(shapeValue)) return all.filter((value) => isContourNoneValue(value));
  return all;
}

function isKnownShape(shape: string, excludes: unknown): boolean {
  return STRAIGHT_CUT_SHAPES.has(shape) || COUNTED_SHAPE_PATTERN.test(shape) || UNCOUNTED_SHAPE_PATTERN.test(shape)
    || contourValuesExcludedForShape(shape, excludes) !== null;
}

/** Of de klant bij deze afwerking een aantal contourlijnen kiest. */
export function shapeUsesContourLines(shape: unknown, values?: Array<string | number>, excludes?: unknown): boolean {
  if (!values) {
    const shapeValue = baseShape(shape);
    return !STRAIGHT_CUT_SHAPES.has(shapeValue) && !UNCOUNTED_SHAPE_PATTERN.test(shapeValue);
  }
  return allowedContourValues(shape, values, excludes).some((value) => !isContourNoneValue(value));
}

/**
 * Waarde die bij de gekozen afwerking hoort. Bij een afwerking met aantal
 * blijft een geldig gekozen aantal staan; anders wordt het het eerste aantal
 * (meestal 1). Zonder aantal wordt het de "geen"-waarde van het product.
 */
export function contourLinesForShape(
  shape: unknown,
  current: unknown,
  values: Array<string | number>,
  excludes?: unknown,
): string | undefined {
  const all = values.map(String);
  if (!all.length) return current === undefined ? undefined : String(current);
  const shapeValue = baseShape(shape);
  // Onbekende vormsnede zonder excludes: de waarde blijft zoals gekozen.
  if (!isKnownShape(shapeValue, excludes) && all.some((value) => isContourNoneValue(value))) {
    return current === undefined ? undefined : String(current);
  }
  const allowed = allowedContourValues(shape, all, excludes);
  const counts = allowed.filter((value) => !isContourNoneValue(value));
  if (!counts.length) return contourNoneValue(allowed.length ? allowed : all) ?? undefined;
  const currentText = String(current ?? '');
  if (currentText && counts.includes(currentText)) return currentText;
  return counts[0];
}
